import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindMenu } from "material-ui-popup-state";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import {
  Button,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Table,
  TableHead,
  Tooltip,
} from "@material-ui/core";
import {
  Business,
  Edit,
  Facebook,
  Instagram,
  WhatsApp,
} from "@material-ui/icons";

import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import EmptyState from "../../components/EmptyState";
import { PageHeader, StatusBadge } from "../../components/ui";
import { AdminMetrics, normalizeSearch } from "../../components/AdminWorkspace";
import { usePanelStyles, FilterBar, SearchField, TableCard, RowActions } from "../../components/FormFields/panel";
import { ConnectionStatus } from "../../components/ConnectionIcon/ConnectionStatus";
import { getTokens } from "../../styles/tokens";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";
import api from "../../services/api";
import WhatsAppModalCompany from "../../components/CompanyWhatsapps";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ForbiddenPage from "../../components/ForbiddenPage";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    count: { color: t.textMuted },
    menuIcon: { marginRight: 10 },
    // linha de totais: fecha a tabela como rodapé
    totalRow: {
      "& .MuiTableCell-root": {
        position: "sticky",
        bottom: 0,
        fontWeight: 600,
        color: t.text,
        backgroundColor: t.muted,
        borderTop: `1px solid ${t.line}`,
      },
    },
  };
});

const AllConnections = () => {
  const classes = useStyles();
  const p = usePanelStyles();
  const { user, socket } = useContext(AuthContext);
  const { list } = useCompanies();
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(true);
  const [loadingComp, setLoadingComp] = useState(false);
  const [whats, setWhats] = useState([]);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [filterConnections, setFilterConnections] = useState([]);
  const [companyWhatsApps, setCompanyWhatsApps] = useState(null);
  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(
    confirmationModalInitialState
  );

  const history = useHistory();
  if (!user.super) {
    history.push("/tickets");
  }

  useEffect(() => {
    setLoadingWhatsapp(true);
    const fetchSession = async () => {
      try {
        const { data } = await api.get("/whatsapp/all/?session=0");
        setWhats(data);
        setLoadingWhatsapp(false);
      } catch (err) {
        setLoadingWhatsapp(false);
        toastError(err);
      }
    };
    fetchSession();
  }, []);

  const responseFacebook = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then((response) => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };
  useEffect(() => {
    loadCompanies();
  }, []);
  const loadCompanies = async () => {
    setLoadingComp(true);
    try {
      const companyList = await list();
      setCompanies(companyList);
    } catch (e) {
      toast.error("Não foi possível carregar a lista de registros");
    }
    setLoadingComp(false);
  };

  const responseInstagram = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          addInstagram: true,
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then((response) => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = (whatsappsFilter, comp) => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
    if (whatsappsFilter?.length > 0) {
      setFilterConnections(whatsappsFilter);
      setCompanyWhatsApps(comp);
    }
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
    setFilterConnections([]);
    setCompanyWhatsApps(null);
  }, [setSelectedWhatsApp, setWhatsAppModalOpen]);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, [setQrModalOpen, setSelectedWhatsApp]);

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId,
      });
    }

    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
  };

  const renderActionButtons = (whatsApp) => {
    return (
      <>
        {whatsApp.status === "qrcode" && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => handleOpenQrModal(whatsApp)}
          >
            {i18n.t("connections.buttons.qrcode")}
          </Button>
        )}
        {whatsApp.status === "DISCONNECTED" && (
          <>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleStartWhatsAppSession(whatsApp.id)}
            >
              {i18n.t("connections.buttons.tryAgain")}
            </Button>{" "}
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => handleRequestNewQrCode(whatsApp.id)}
            >
              {i18n.t("connections.buttons.newQr")}
            </Button>
          </>
        )}
        {(whatsApp.status === "CONNECTED" ||
          whatsApp.status === "PAIRING" ||
          whatsApp.status === "TIMEOUT") && (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={() => {
              handleOpenConfirmationModal("disconnect", whatsApp.id);
            }}
          >
            {i18n.t("connections.buttons.disconnect")}
          </Button>
        )}
        {whatsApp.status === "OPENING" && (
          <Button size="small" variant="outlined" disabled color="default">
            {i18n.t("connections.buttons.connecting")}
          </Button>
        )}
      </>
    );
  };

  const renderStatusToolTips = (whatsApp) => {
    return <ConnectionStatus status={whatsApp.status} />;
  };

  const countOf = (company, test) =>
    whats?.length && whats.filter((item) => item?.companyId === company?.id && test(item)).length;
  const isConnected = (item) => item?.status === "CONNECTED";
  const visibleCompanies = (companies || []).filter((company) =>
    normalizeSearch(company?.name).includes(normalizeSearch(searchParam))
  );
  const totalConnected = whats?.length && whats.filter(isConnected).length;
  const totalDisconnected = whats?.length && whats.filter((item) => !isConnected(item)).length;
  const columns = user.profile === "admin" ? 5 : 4;

  // número de conexões: selo no tom do estado quando há alguma, zero discreto quando não há
  const renderCount = (value, tone) =>
    value > 0 ? <StatusBadge color={tone} label={String(value)} /> : <span className={classes.count}>{value || 0}</span>;

  return (
    <MainContainer maxWidth>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      <QrcodeModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
      />
      <WhatsAppModalCompany
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        filteredWhatsapps={filterConnections}
        companyInfos={companyWhatsApps}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />

      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <PageHeader
            title={i18n.t("connections.title")}
            subtitle={i18n.t(
              "connections.connectYourServiceChannelsToReceiveMessagesAndStartConversationsWithYourCustomers"
            )}
          />
          {/* Menu de criação herdado: nunca teve botão que o abrisse nesta tela; mantido como estava. */}
          <PopupState variant="popover" popupId="demo-popup-menu">
            {(popupState) => (
              <React.Fragment>
                <Menu {...bindMenu(popupState)}>
                  <MenuItem
                    onClick={() => {
                      handleOpenWhatsAppModal();
                      popupState.close();
                    }}
                  >
                    <WhatsApp fontSize="small" className={classes.menuIcon} />
                    WhatsApp
                  </MenuItem>
                  <FacebookLogin
                    appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                    autoLoad={false}
                    fields="name,email,picture"
                    version="13.0"
                    scope="public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                    callback={responseFacebook}
                    render={(renderProps) => (
                      <MenuItem onClick={renderProps.onClick}>
                        <Facebook fontSize="small" className={classes.menuIcon} />
                        Facebook
                      </MenuItem>
                    )}
                  />

                  <FacebookLogin
                    appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                    autoLoad={false}
                    fields="name,email,picture"
                    version="13.0"
                    scope="public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                    callback={responseInstagram}
                    render={(renderProps) => (
                      <MenuItem onClick={renderProps.onClick}>
                        <Instagram fontSize="small" className={classes.menuIcon} />
                        Instagram
                      </MenuItem>
                    )}
                  />
                </Menu>
              </React.Fragment>
            )}
          </PopupState>
          <AdminMetrics
            items={[
              { value: companies?.length || 0, label: "Empresas" },
              { value: (whats?.length && whats.length) || 0, label: i18n.t("connections.totalConnections").trim() },
              { value: totalConnected || 0, label: i18n.t("connections.connectedConnections").trim() },
              { value: totalDisconnected || 0, label: i18n.t("connections.disconnectedConnections").trim() },
            ]}
          />
          <FilterBar>
            <SearchField
              value={searchParam}
              onChange={(event) => setSearchParam(event.target.value)}
              placeholder="Buscar empresa…"
            />
          </FilterBar>
          <TableCard fill>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("connections.client")}</TableCell>
                  <TableCell>{i18n.t("connections.connectedConnections")}</TableCell>
                  <TableCell>{i18n.t("connections.disconnectedConnections")}</TableCell>
                  <TableCell>{i18n.t("connections.totalConnections")}</TableCell>
                  {user.profile === "admin" && (
                    <TableCell align="right">{i18n.t("connections.table.actions")}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingWhatsapp ? (
                  <TableRowSkeleton columns={columns} />
                ) : (
                  <>
                    {visibleCompanies.length > 0 &&
                      visibleCompanies.map((company) => (
                        <TableRow key={company.id}>
                          <TableCell className={p.cellStrong}>{company?.name}</TableCell>
                          <TableCell>{renderCount(countOf(company, isConnected), "success")}</TableCell>
                          <TableCell>{renderCount(countOf(company, (item) => !isConnected(item)), "error")}</TableCell>
                          <TableCell>{countOf(company, () => true)}</TableCell>
                          {user.profile === "admin" && (
                            <TableCell align="right">
                              <RowActions>
                                <Tooltip title="Ver conexões">
                                  <IconButton
                                    size="small"
                                    aria-label={`Ver conexões de ${company?.name}`}
                                    onClick={() =>
                                      handleOpenWhatsAppModal(
                                        whats.filter(
                                          (item) =>
                                            item?.companyId === company?.id
                                        ),
                                        company
                                      )
                                    }
                                  >
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                              </RowActions>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    {!visibleCompanies.length && (
                      <TableRow>
                        <TableCell colSpan={columns}>
                          <EmptyState
                            icon={<Business />}
                            title={companies?.length ? "Nenhuma empresa corresponde à busca" : "Nenhuma empresa cadastrada"}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow className={classes.totalRow}>
                      <TableCell>{i18n.t("connections.total")}</TableCell>
                      <TableCell>{totalConnected}</TableCell>
                      <TableCell>{totalDisconnected}</TableCell>
                      <TableCell>{whats?.length && whats.length}</TableCell>
                      {user.profile === "admin" && <TableCell />}
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableCard>
        </>
      )}
    </MainContainer>
  );
};

export default AllConnections;
